import type {Descendant} from 'slate';
import {slateNodeToText} from '@common/lib/slateText';
import type {SlateValue} from '@common-web/lib/slate-types';

export const EMPTY_SLATE_VALUE: SlateValue = [{type: 'paragraph', children: [{text: ''}]}];

/**
 * Safely parse a body string into a Slate value. Bodies are stored as
 * `JSON.stringify(Descendant[])`. Returns a single empty paragraph if the
 * string is missing or not valid Slate JSON.
 */
export function parseSlateBody(body: string | null | undefined): SlateValue {
  if (!body) return EMPTY_SLATE_VALUE;
  try {
    const parsed = JSON.parse(body);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed as SlateValue;
    }
  } catch {
    // fall through
  }
  return EMPTY_SLATE_VALUE;
}

/**
 * Cheap heuristic: does this body string look like a JSON-encoded Slate value?
 * Used by the migration to skip already-migrated rows.
 */
export function looksLikeSlateBody(body: string | null | undefined): boolean {
  if (!body) return false;
  const trimmed = body.trim();
  if (!trimmed.startsWith('[')) return false;
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) && parsed.every((n) => typeof n === 'object' && n !== null);
  } catch {
    return false;
  }
}

/**
 * Convert a Slate value to a plain-text string. Used for list previews and
 * email body fallbacks where the rich-text formatting must be flattened.
 *
 * Delegates to the API's shared flattener (`@common/lib/slateText`), which
 * renders mention elements as their display text (`@userName`) — keeping this
 * in agreement with the server's `slateBodyToText` so a mention-only note
 * isn't rejected server-side as empty.
 */
export function slateToPlainText(value: SlateValue): string {
  return value
    .map((node) => slateNodeToText(node))
    .join('\n')
    .trim();
}

export function isSlateValueEmpty(value: SlateValue): boolean {
  return slateToPlainText(value).length === 0;
}

/**
 * Build an empty Slate value containing a single paragraph wrapping the given
 * text. Useful for migrating legacy plaintext content.
 */
export function plainTextToSlate(text: string): SlateValue {
  if (!text.trim()) return EMPTY_SLATE_VALUE;
  return text.split('\n').map((line) => ({
    type: 'paragraph' as const,
    children: [{text: line}],
  }));
}

/**
 * Re-export a constructor so consumers don't have to import the Slate Node helpers directly.
 */
export type {Descendant};
